import { ADS_BATCH_MAX_ITEMS, AdsBatchRepeatInputBody, AdsCreativeItem, } from '@wit-support/admin-contracts';
/**
 * Адрес читается «сырой» строкой, а не разобранным `search` роутера: роутер превращает
 * цифровые значения в числа (id кампании Meta — 18 цифр, он теряет точность и портится),
 * а строки из цифр, которые он сам записывает, берёт в кавычки — они снимаются.
 */
export function rawSearchOf(searchStr) {
    const out = {};
    for (const [k, v] of new URLSearchParams(searchStr)) {
        out[k] = v.length >= 2 && v.startsWith('"') && v.endsWith('"') ? v.slice(1, -1) : v;
    }
    return out;
}
const OBJECT_ID = /^[a-f0-9]{24}$/;
const CAMPAIGN_ID = /^\d{1,30}$/;
/**
 * `/new?duplicateFrom=<campaignId>&account=<platformAccountId>` — подготовить копию;
 * `/new?duplicate=<templateId>` — открыть уже подготовленную. Нет ни того ни другого — null
 * (обычный «Новый запуск»); параметр есть, но не по форме — `invalid`.
 */
export function parseDuplicateTarget(search) {
    const { duplicate, duplicateFrom, account } = search;
    const has = (v) => v != null && v !== '';
    if (has(duplicate)) {
        return typeof duplicate === 'string' && OBJECT_ID.test(duplicate)
            ? { kind: 'template', templateId: duplicate }
            : { kind: 'invalid' };
    }
    if (has(duplicateFrom)) {
        return typeof duplicateFrom === 'string' &&
            CAMPAIGN_ID.test(duplicateFrom) &&
            typeof account === 'string' &&
            OBJECT_ID.test(account)
            ? { kind: 'campaign', campaignId: duplicateFrom, platformAccountId: account }
            : { kind: 'invalid' };
    }
    return null;
}
const IDEMPOTENCY_RE = /^[A-Za-z0-9_-]{8,64}$/;
/**
 * Поля входа пачки, которые копия подставляет в форму. Остальное (лишние ключи, `source`,
 * `idempotencyKey`) отбрасывается; каждое поле проверяется своей схемой отдельно — одно
 * негодное значение не лишает формы остального (мягкий разбор, а не одна строгая схема).
 */
const INPUT_KEYS = [
    'platformAccountIds',
    'overrides',
    'copies',
    'layout',
    'start',
    'funnelSettings',
    'bidStepMinor',
    'bidStepDirection',
    'bidFloorMinor',
    'bidCeilingMinor',
    'adSetGroups',
];
function lenientInput(template, batchInput) {
    const raw = batchInput ?? {};
    const shape = AdsBatchRepeatInputBody.shape;
    const picked = {};
    for (const k of INPUT_KEYS) {
        if (!(k in raw))
            continue;
        const parsed = shape[k].safeParse(raw[k]);
        if (parsed.success && parsed.data !== undefined)
            picked[k] = parsed.data;
    }
    const rawItems = Array.isArray(raw['items']) ? raw['items'] : [];
    const validItems = rawItems
        .map((it) => AdsCreativeItem.safeParse(it))
        .flatMap((r) => (r.success ? [r.data] : []));
    const items = validItems.slice(0, ADS_BATCH_MAX_ITEMS);
    const accounts = Array.isArray(picked['platformAccountIds'])
        ? picked['platformAccountIds']
        : [];
    const input = AdsBatchRepeatInputBody.parse({
        // шаблон и версия — всегда текущие: копию собирает форма на открытом шаблоне
        ...picked,
        templateId: template.id,
        templateVersion: template.version,
        platformAccountIds: accounts.length > 0 ? accounts.slice(0, 10) : template.platformAccountIds.slice(0, 10),
        items,
        overrides: picked['overrides'] ?? {},
        start: picked['start'] ?? { mode: 'now' },
        // как велит K2: копия — всегда вручную и черновик
        activation: 'manual',
        mode: 'draft',
    });
    return {
        input,
        items: { total: rawItems.length, valid: validItems.length, taken: items.length },
    };
}
/**
 * Вход обычной формы запуска для копии из `batchInput` ответа `GET /duplicates/:id`. Разбор
 * мягкий (`lenientInput`): посты ограничены лимитом пачки, незнакомые ключи отброшены, негодное
 * поле не роняет форму. Кабинет без подходящих значений — кабинеты шаблона. В любом случае
 * включение — `manual`, режим — черновик.
 */
export function duplicateRepeat(template, batchInput) {
    const { input, items } = lenientInput(template, batchInput);
    const key = batchInput?.['idempotencyKey'];
    return {
        data: {
            repeat: {
                // «источник» нужен только плашке повтора, которой у копии нет
                source: {
                    batchId: template.id,
                    launchId: null,
                    createdAt: template.createdAt,
                    templateName: template.name,
                    templateVersionThen: template.version,
                    mode: 'draft',
                    activation: 'manual',
                },
                template: {
                    id: template.id,
                    name: template.name,
                    version: template.version,
                    archived: template.archived,
                },
                input,
                newGroupKeys: [],
                spend: { before: [], after: null },
                notes: [],
            },
            files: {},
        },
        idempotencyKey: typeof key === 'string' && IDEMPOTENCY_RE.test(key) ? key : undefined,
        fromBatchInput: items.taken > 0,
        items,
    };
}
//# sourceMappingURL=duplicate-flow.js.map